import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  createWalletAlert, 
  getWalletAlerts, 
  deleteWalletAlert,
  getWalletAlertHistory,
  triggerWalletAlert
} from '@/lib/wallet-alert-api';
import { useToast } from '@/hooks/use-toast';
import { WalletAlert, WalletAlertHistory } from '@shared/schema';

/**
 * Interface for creating a new wallet alert
 * We are now only supporting Bitcoin with transaction-based alert types
 */
interface CreateWalletAlertDto {
  address: string;
  cryptocurrency: 'bitcoin';
  alertType: 'received' | 'sent' | 'both';
  notificationMethod: 'email' | 'sms' | 'both';
  contactInfo: string;
}

export function useWalletAlerts() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Query to fetch wallet alerts
  const {
    data: walletAlerts = [],
    isLoading,
    error,
    refetch,
  } = useQuery<WalletAlert[]>({
    queryKey: ['/api/wallet-alerts'],
    queryFn: getWalletAlerts,
    // Handle errors separately
    staleTime: 60000, // 1 minute
    retry: 1,
  });
  
  // Query to fetch wallet alert history
  const {
    data: walletAlertHistory = [],
    isLoading: isLoadingHistory,
    error: historyError,
    refetch: refetchHistory,
  } = useQuery<WalletAlertHistory[]>({
    queryKey: ['/api/wallet-alerts/history'],
    queryFn: () => getWalletAlertHistory(),
    staleTime: 60000, // 1 minute
    retry: 1,
  });
  
  // Mutation to create a wallet alert
  const createMutation = useMutation({
    mutationFn: (data: CreateWalletAlertDto) => createWalletAlert(data),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Wallet alert created successfully',
      });
      // Invalidate and refetch alerts after creating a new one
      queryClient.invalidateQueries({ queryKey: ['/api/wallet-alerts'] });
      refetch();
    },
    onError: (error: Error) => {
      if (error.message.includes('already exists')) {
        toast({
          title: 'Error',
          description: `An alert already exists for this address. Please delete the existing alert before creating a new one.`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: `Failed to create wallet alert: ${error.message}`,
          variant: 'destructive',
        });
      }
    },
  });
  
  // Mutation to delete a wallet alert
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteWalletAlert(id),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Wallet alert deleted successfully',
      });
      // Invalidate and refetch alerts after deleting one
      queryClient.invalidateQueries({ queryKey: ['/api/wallet-alerts'] });
      refetch();
    },
    onError: (error: Error) => {
      console.error('Error deleting wallet alert:', error);
      toast({
        title: 'Error',
        description: `Failed to delete wallet alert: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
  
  // Mutation to trigger a wallet alert (for testing)
  const triggerMutation = useMutation({
    mutationFn: (id: number) => triggerWalletAlert(id),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Wallet alert triggered successfully',
      });
      // Refetch alerts and history after triggering an alert
      refetch();
      refetchHistory();
    },
    onError: (error: Error) => {
      console.error('Error triggering wallet alert:', error);
      toast({
        title: 'Error',
        description: `Failed to trigger wallet alert: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
  
  return {
    // Active alerts data
    walletAlerts,
    isLoading,
    error,
    createAlert: createMutation.mutate,
    deleteAlert: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
    
    // Alert history data
    walletAlertHistory,
    isLoadingHistory,
    historyError,
    triggerAlert: triggerMutation.mutate,
    isTriggering: triggerMutation.isPending,
  };
}